import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import './index.css';
import { initializeApiClient } from '@signalvote/api-client';
import {
  SUBSIDIARY_CODE,
  SENTRY_DSN,
  META_PIXEL_ID,
  GTM_CONTAINER_ID,
} from './config';
import { queryClient } from './lib/queryClient';
import { webApiClientConfig } from './lib/apiClientConfig';
import { installServerWrites } from './lib/serverWrites';
import { initMetaPixel } from './lib/metaPixel';
import { initGtm } from './lib/gtm';
import { routeTree } from './routeTree.gen';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Import theme CSS - must be done before React renders
import './themes';

// Only initialize Sentry in production builds. Sentry's own guidance is to call
// init conditionally on the environment rather than rely on `enabled: false`,
// which still leaves instrumentation overhead in place. This keeps local dev from
// uploading errors and Session Replay screen recordings to the shared project.
if (import.meta.env.PROD && SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SUBSIDIARY_CODE,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
  });
}

// Load the Meta ad pixel only in production builds that have a pixel id configured
// (set per-brand at build time, e.g. .env.gop for the Republican warming pilot). Fires
// a PageView so visitors arriving from the warming ads can be retargeted. No-op otherwise.
if (import.meta.env.PROD && META_PIXEL_ID) {
  initMetaPixel(META_PIXEL_ID);
}

// Load Google Tag Manager only in production builds that have a container id
// configured (set per-brand at build time, e.g. .env.gop for the Republican
// warming pilot). No-op otherwise.
if (import.meta.env.PROD && GTM_CONTAINER_ID) {
  initGtm(GTM_CONTAINER_ID);
}

// Set subsidiary attribute on document root BEFORE React renders
// This activates the correct CSS variables for the theme
document.documentElement.setAttribute('data-subsidiary', SUBSIDIARY_CODE);

// Set favicon at runtime (supplements build-time injection for dev server / SPA navigation)
const faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
if (faviconLink) {
  faviconLink.href = `/brands/${SUBSIDIARY_CODE}/favicon.svg`;
  faviconLink.type = 'image/svg+xml';
}

// Initialize the API client before rendering
installServerWrites(queryClient);
initializeApiClient(webApiClientConfig());

// Create the TanStack Router instance
export const router = createRouter({
  routeTree,
  context: {
    auth: undefined!, // Will be set by InnerApp
    queryClient,
  },
  defaultPreload: 'intent',
});

// Register the router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Inner component that has access to AuthContext
function InnerApp() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// TanStack Router handles all routes
// React Router has been completely removed
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InnerApp />
        <ReactQueryDevtools initialIsOpen={false} />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
